import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { Loader2, Upload, Eye, Pencil, Save, Send, EyeOff, X } from 'lucide-react';
import {
  getPost,
  createPost,
  updatePost,
  listCategories,
  uploadCover,
  slugify,
  type BlogPost,
  type BlogCategory,
} from './blogApi';

const PREVIEW_CSS = `
.blog-md-preview { color: var(--fd-text); font-size: 15px; line-height: 1.7; }
.blog-md-preview > * + * { margin-top: 1em; }
.blog-md-preview h1,.blog-md-preview h2,.blog-md-preview h3,.blog-md-preview h4 { color:var(--fd-text-bright); font-weight:700; line-height:1.25; margin-top:1.4em; }
.blog-md-preview h1 { font-size:1.7em; } .blog-md-preview h2 { font-size:1.4em; } .blog-md-preview h3 { font-size:1.2em; }
.blog-md-preview a { color:var(--fd-gold); text-decoration:underline; }
.blog-md-preview strong { color:var(--fd-text-bright); }
.blog-md-preview ul,.blog-md-preview ol { padding-left:1.4em; } .blog-md-preview ul { list-style:disc; } .blog-md-preview ol { list-style:decimal; }
.blog-md-preview li { margin-top:.35em; } .blog-md-preview li::marker { color:var(--fd-gold); }
.blog-md-preview blockquote { border-left:3px solid var(--fd-gold); padding-left:1em; color:var(--fd-text-soft); font-style:italic; }
.blog-md-preview code { background:var(--fd-surface); border:1px solid var(--fd-border); border-radius:5px; padding:.1em .4em; font-size:.9em; }
.blog-md-preview pre { background:var(--fd-surface); border:1px solid var(--fd-border); border-radius:10px; padding:1em; overflow-x:auto; }
.blog-md-preview pre code { background:none; border:none; padding:0; }
.blog-md-preview img { border-radius:10px; max-width:100%; }
.blog-md-preview table { width:100%; border-collapse:collapse; display:block; overflow-x:auto; }
.blog-md-preview th,.blog-md-preview td { border:1px solid var(--fd-border); padding:.6em .8em; text-align:left; }
.blog-md-preview th { background:var(--fd-glass-1); color:var(--fd-text-bright); }
`;

const inputCls =
  'w-full rounded-lg border border-line bg-canvas px-4 h-11 text-fg outline-none transition-colors focus:border-gold focus:shadow-focus';
const labelCls =
  'mb-2 block font-display text-xs font-semibold uppercase tracking-[0.12em] text-fg-3';

export function BlogEditor({ basePath = '/admin/blog' }: { basePath?: string } = {}) {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Form state
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [categorySlug, setCategorySlug] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [coverUrl, setCoverUrl] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [status, setStatusState] = useState<'draft' | 'published'>('draft');
  const [publishedAt, setPublishedAt] = useState<string | null>(null);
  const slugTouched = useRef(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const cats = await listCategories();
        if (!active) return;
        setCategories(cats);
        if (isNew && cats.length) setCategorySlug(cats[0].slug);

        if (!isNew && id) {
          const post = await getPost(id);
          if (!active) return;
          if (!post) {
            toast.error('Post not found');
            navigate(basePath || '/');
            return;
          }
          hydrate(post);
        }
      } catch (e) {
        toast.error((e as Error).message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function hydrate(post: BlogPost) {
    setTitle(post.title);
    setSlug(post.slug);
    setCategorySlug(post.category_slug);
    setContent(post.content);
    setExcerpt(post.excerpt ?? '');
    setCoverUrl(post.cover_image_url ?? '');
    setMetaTitle(post.meta_title ?? '');
    setMetaDescription(post.meta_description ?? '');
    setAuthorName(post.author_name ?? '');
    setStatusState(post.status);
    setPublishedAt(post.published_at);
    slugTouched.current = true;
  }

  const onTitleChange = (v: string) => {
    setTitle(v);
    if (!slugTouched.current) setSlug(slugify(v));
  };

  const onPickFile = () => fileRef.current?.click();
  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadCover(file);
      setCoverUrl(url);
      toast.success('Cover uploaded');
    } catch (err) {
      toast.error(`Upload failed: ${(err as Error).message}`);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const save = useCallback(
    async (nextStatus: 'draft' | 'published') => {
      if (!title.trim()) return toast.error('Title is required');
      const finalSlug = (slug.trim() || slugify(title)).trim();
      if (!finalSlug) return toast.error('Slug is required');
      if (!categorySlug) return toast.error('Pick a category');

      setSaving(true);
      const input = {
        title: title.trim(),
        slug: finalSlug,
        category_slug: categorySlug,
        content,
        excerpt: excerpt.trim() || null,
        cover_image_url: coverUrl.trim() || null,
        status: nextStatus,
        meta_title: metaTitle.trim() || null,
        meta_description: metaDescription.trim() || null,
        author_name: authorName.trim() || null,
      };
      try {
        if (isNew) {
          const created = await createPost(input);
          toast.success(nextStatus === 'published' ? 'Published' : 'Draft saved');
          navigate(`${basePath}/${created.id}`, { replace: true });
        } else if (id) {
          const updated = await updatePost(id, input, { existingPublishedAt: publishedAt });
          hydrate(updated);
          toast.success(nextStatus === 'published' ? 'Published' : 'Saved');
        }
      } catch (e) {
        const msg = (e as Error).message;
        toast.error(/duplicate key|unique/i.test(msg) ? 'That slug is already taken' : msg);
      } finally {
        setSaving(false);
      }
    },
    [title, slug, categorySlug, content, excerpt, coverUrl, metaTitle, metaDescription, authorName, isNew, id, publishedAt, navigate, basePath],
  );

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-7 w-7 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div>
      <style>{PREVIEW_CSS}</style>

      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-fg">
            {isNew ? 'New post' : 'Edit post'}
          </h1>
          {!isNew && (
            <span
              className={`mt-1 inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${
                status === 'published'
                  ? 'bg-success/15 text-success'
                  : 'bg-tint/5 text-fg-3'
              }`}
            >
              {status}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => save('draft')}
            disabled={saving}
            className="inline-flex h-11 items-center gap-2 rounded-lg border border-line px-4 font-display font-semibold text-fg hover:border-gold/40 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            Save as draft
          </button>
          {status === 'published' ? (
            <>
              <button
                onClick={() => save('published')}
                disabled={saving}
                className="inline-flex h-11 items-center gap-2 rounded-lg bg-gold-fill px-5 font-display font-semibold text-on-gold hover:bg-gold-hi disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Update
              </button>
              <button
                onClick={() => save('draft')}
                disabled={saving}
                title="Unpublish (keeps the original publish date)"
                className="inline-flex h-11 items-center gap-2 rounded-lg border border-line px-4 font-display font-semibold text-fg-3 hover:text-fg disabled:opacity-50"
              >
                <EyeOff className="h-4 w-4" />
                Unpublish
              </button>
            </>
          ) : (
            <button
              onClick={() => save('published')}
              disabled={saving}
              className="inline-flex h-11 items-center gap-2 rounded-lg bg-gold-fill px-5 font-display font-semibold text-on-gold hover:bg-gold-hi disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Publish
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-[1fr_320px]">
        {/* Main column */}
        <div className="space-y-6">
          <div>
            <label className={labelCls}>Title</label>
            <input
              className={inputCls}
              value={title}
              onChange={(e) => onTitleChange(e.target.value)}
              placeholder="How to register as a sole trader"
            />
          </div>

          <div>
            <label className={labelCls}>Slug</label>
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm text-fg-3">/blog/{categorySlug || '…'}/</span>
              <input
                className={inputCls}
                value={slug}
                onChange={(e) => { slugTouched.current = true; setSlug(slugify(e.target.value)); }}
                placeholder="how-to-register-as-a-sole-trader"
              />
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className={`${labelCls} mb-0`}>Content (Markdown)</label>
              <button
                type="button"
                onClick={() => setShowPreview((v) => !v)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-gold hover:underline"
              >
                {showPreview ? <Pencil className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {showPreview ? 'Edit' : 'Preview'}
              </button>
            </div>
            {showPreview ? (
              <div className="min-h-[420px] rounded-lg border border-line bg-canvas p-5">
                <div className="blog-md-preview">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
                    {content || '_Nothing to preview yet._'}
                  </ReactMarkdown>
                </div>
              </div>
            ) : (
              <textarea
                className="min-h-[420px] w-full rounded-lg border border-line bg-canvas p-4 font-mono text-sm leading-relaxed text-fg outline-none transition-colors focus:border-gold focus:shadow-focus"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={'## Heading\n\nWrite your post in **Markdown**.\n\n- Bullet\n- Points\n\n> Tip callout'}
              />
            )}
          </div>

          <div>
            <label className={labelCls}>Excerpt</label>
            <textarea
              className="min-h-[80px] w-full rounded-lg border border-line bg-canvas p-4 text-sm text-fg outline-none focus:border-gold"
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              placeholder="Short summary shown on listing pages and in search results."
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div>
            <label className={labelCls}>Category</label>
            <select
              className={`${inputCls} cursor-pointer`}
              value={categorySlug}
              onChange={(e) => setCategorySlug(e.target.value)}
            >
              {categories.map((c) => (
                <option key={c.slug} value={c.slug} className="bg-menu">
                  {c.display_name}{c.is_localized ? ' (localised)' : ''}
                </option>
              ))}
            </select>
            {categories.find((c) => c.slug === categorySlug)?.is_localized && (
              <p className="mt-2 text-xs leading-relaxed text-fg-3">
                This category generates local town pages (e.g. “…-in-manchester”) automatically.
              </p>
            )}
          </div>

          <div>
            <label className={labelCls}>Author</label>
            <input
              className={inputCls}
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              placeholder="e.g. Kaspars"
            />
          </div>

          <div>
            <label className={labelCls}>Cover image</label>
            {coverUrl ? (
              <div className="relative overflow-hidden rounded-lg border border-line">
                <img src={coverUrl} alt="Cover" className="aspect-[16/9] w-full object-cover" />
                <button
                  onClick={() => setCoverUrl('')}
                  className="absolute right-2 top-2 rounded-full bg-scrim/60 p-1.5 text-fg hover:bg-scrim/80"
                  title="Remove"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={onPickFile}
                disabled={uploading}
                className="flex aspect-[16/9] w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-line text-fg-3 hover:border-gold/40 hover:text-fg disabled:opacity-50"
              >
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                <span className="text-xs">{uploading ? 'Uploading…' : 'Upload cover'}</span>
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
          </div>

          <div className="rounded-lg border border-line p-4">
            <p className="mb-3 font-display text-xs font-semibold uppercase tracking-[0.12em] text-fg-3">
              SEO
            </p>
            <label className={labelCls}>Meta title</label>
            <input
              className={inputCls}
              value={metaTitle}
              onChange={(e) => setMetaTitle(e.target.value)}
              placeholder="Falls back to the title"
            />
            <label className={`${labelCls} mt-4`}>Meta description</label>
            <textarea
              className="min-h-[80px] w-full rounded-lg border border-line bg-canvas p-3 text-sm text-fg outline-none focus:border-gold"
              value={metaDescription}
              onChange={(e) => setMetaDescription(e.target.value)}
              placeholder="~150 chars. Falls back to the excerpt."
            />
            <p className="mt-1 text-right text-xs text-fg-3">{metaDescription.length} chars</p>
          </div>
        </div>
      </div>
    </div>
  );
}
